import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type { DialogConfig, DialogType } from "./types";

// Re-export types for backward compatibility
export type { DialogConfig, DialogType } from "./types";

export interface DialogState {
  // Current active dialogs stack (supports nested dialogs)
  dialogStack: DialogConfig[];

  // Actions
  openDialog: (config: DialogConfig) => void;
  closeDialog: (type?: DialogType) => void;
  closeAllDialogs: () => void;
  replaceDialog: (config: DialogConfig) => void;

  // Getters
  isDialogOpen: (type: DialogType) => boolean;
  getDialogConfig: (type: DialogType) => DialogConfig | null;
  getCurrentDialog: () => DialogConfig | null;
}

export const useDialogRouterStore = create<DialogState>()(
  devtools(
    (set, get) => ({
      dialogStack: [],

      openDialog: (config: DialogConfig) => {
        set((state) => ({
          dialogStack: [...state.dialogStack, config],
        }));
      },

      closeDialog: (type?: DialogType) => {
        set((state) => {
          if (type) {
            // Close specific dialog type
            return {
              dialogStack: state.dialogStack.filter((dialog) => dialog.type !== type),
            };
          }
          // Close last dialog in stack
          return {
            dialogStack: state.dialogStack.slice(0, -1),
          };
        });
      },

      closeAllDialogs: () => {
        set({ dialogStack: [] });
      },

      replaceDialog: (config: DialogConfig) => {
        set((state) => ({
          dialogStack: [...state.dialogStack.slice(0, -1), config],
        }));
      },

      isDialogOpen: (type: DialogType) => {
        const state = get();
        return state.dialogStack.some((dialog) => dialog.type === type);
      },

      getDialogConfig: (type: DialogType) => {
        const state = get();
        return state.dialogStack.find((dialog) => dialog.type === type) || null;
      },

      getCurrentDialog: () => {
        const state = get();
        return state.dialogStack[state.dialogStack.length - 1] || null;
      },
    }),
    {
      name: "dialog-router-store",
    }
  )
);
